import { treatErrorAs, handleErrorsInQueue } from 'api/errors';
import generateUuid from 'uuid/v4';

// constants
import { errorLevel } from 'constants/errors';
import { resourceType } from 'constants/loading';
import { scriptTypes } from 'constants/scripts';

// actions
import {
  setBaseManagementZones,
  setImportedManagementZones,
  setScriptBasedManagementZones,
  setActiveManagementZonesForScriptType
} from 'manual';
import {
  setResourceLoaded, setResourcePending
} from 'loading';

// finders
import {
  findImportedMapsImageryByFieldId,
  findSsurgoImageryByFieldId
} from 'imagery/finders';
import {
  findManualGeneratedZones,
  findCompleteMapLayersJobId,
  findCompleteLayers,
  findAvailableLayers
} from 'manual/finders';

// requesters
import { requestActivities } from 'activities/requesters';
import { requestFertilityPracticesData } from 'products/requesters';
import { requestManualFlowPrimaryScripts } from 'scripts/requesters';

// selectors
import { getActiveFieldId, getActiveScriptType, getCompleteMapLayersId, getActiveUserId } from 'current/selectors';
import {
  getBaseManagementZones,
  getCompleteMapYear,
  getImportedManagementZones,
  getScriptBasedManagementZones,
  isMultiLayerZoneGenerationEnabled
} from 'manual/selectors';

export const requestCompleteMapsJob = (fieldId, year) => {
  return (dispatch, getState) => {
    return Promise.resolve().then(() => {
      return findCompleteMapLayersJobId({dispatch, getState, fieldId, year});
    }).catch( treatErrorAs({ severity: errorLevel.INFO }));
  };
};

export const requestManagementZones = () => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldId = getActiveFieldId(state);
    const currentYear = new Date().getFullYear();
    const isMultiLayerEnabled = isMultiLayerZoneGenerationEnabled(state);

    return Promise.resolve()
    .then(() => {

      const promises = [
        dispatch(requestActivities()),
        dispatch(requestManualFlowPrimaryScripts()),
        (!isMultiLayerEnabled && dispatch(requestCompleteMapsJob(fieldId, currentYear))),
        findImportedMapsImageryByFieldId({ dispatch, getState, fieldId, forceWait: true })
        .catch( treatErrorAs({ severity: errorLevel.INFO })),
        findSsurgoImageryByFieldId({ dispatch, getState, fieldId }),
        (isMultiLayerEnabled && findAvailableLayers({ dispatch, getState }))
      ];

      const pendingPromise = Promise.all(promises);
      const type = resourceType.MANAGEMENT_ZONES;
      const id = generateUuid();

      dispatch(setResourcePending({
        type,
        id,
        promise: pendingPromise,
        exclusive: true
      }));

      return pendingPromise
      .then(() => {
        const state = getState();
        dispatch(setBaseManagementZones(getBaseManagementZones(state)));
        dispatch(setScriptBasedManagementZones(getScriptBasedManagementZones(state)));
        dispatch(setImportedManagementZones(getImportedManagementZones(state)));
      })
      .then(() => {
        dispatch(setActiveManagementZonesForScriptType());
      })
      .then(() => dispatch(setResourceLoaded({ type, id })) )
      .catch( treatErrorAs({ severity: errorLevel.BLOCK, andStopHere:true }) )
      .then( () => handleErrorsInQueue() );

    });
  };
};

export const requestManualPlantingPlanFormData = () => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptType = getActiveScriptType(state);

    return Promise.resolve()
    .then(() => {

      dispatch(requestActivities());

      if (scriptType === scriptTypes.FERTILITY) {
        dispatch(requestFertilityPracticesData());
      }
    })
    .catch( treatErrorAs({ severity: errorLevel.BLOCK, andStopHere:true }) )
    .then( () => handleErrorsInQueue() );
  };
};

export const requestManualGeneratedZones = (numZones) => {
  return (dispatch, getState) => {
    const type = resourceType.MAP_ZONES;
    const id = generateUuid();

    return Promise.resolve()
    .then(() => {
      const pendingPromise = Promise.all([
        findManualGeneratedZones({dispatch, getState, numZones})
      ]);

      dispatch(setResourcePending({
        type,
        id,
        promise: pendingPromise,
        exclusive: true
      }));

      return pendingPromise
      .then(() => {
        dispatch(setResourceLoaded({ type, id }));
      });

    }).catch( treatErrorAs({ severity: errorLevel.BLOCK }));
  };
};

export const requestCompleteMapLayers = () => {

  return (dispatch, getState) => {
    const state = getState();
    const jobId = getCompleteMapLayersId(state);
    const id = generateUuid();
    const type = resourceType.COMPLETE_MAP_LAYERS;
    const userId = getActiveUserId(state);
    const fieldId = getActiveFieldId(state);
    const year = getCompleteMapYear(state);

    return Promise.resolve().then(() => {

      const pendingPromise = Promise.all([
        findCompleteLayers({dispatch, jobId, userId, fieldId, year}).catch( treatErrorAs({ severity: errorLevel.INFO }))
      ]);

      dispatch(setResourcePending({
        type,
        id,
        promise: pendingPromise,
        exclusive: true
      }));

      return pendingPromise
      .then(() => dispatch(setResourceLoaded({ type, id })) );

    }).catch( treatErrorAs({ severity: errorLevel.INFO }));
  };
};
